import type { GamePlan, Team } from '../types';

/**
 * Generate the formatted title for a game plan based on home/away status
 * @param gamePlan - The game plan object
 * @param currentTeam - The current team object
 * @returns Formatted title string
 */
export function getGamePlanTitle(gamePlan: GamePlan, currentTeam?: Team | null): string {
  // If we don't have both team name and opponent name, fall back to original title
  if (!currentTeam?.name || !gamePlan.opponent_name) {
    return gamePlan.title || 'Untitled Game Plan';
  }

  // Format based on home/away status
  const homeAway = gamePlan.home_away || 'home';
  const separator = homeAway === 'home' ? 'vs' : '@';
  
  return `${currentTeam.name} ${separator} ${gamePlan.opponent_name}`;
}

/**
 * Sort game plans with smart ordering:
 * 1. Future games (ascending by date)
 * 2. Past games (descending by date)
 */
export function sortGamePlans(gamePlans: GamePlan[]): GamePlan[] {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  
  // Separate future and past games
  const futureGames: GamePlan[] = [];
  const pastGames: GamePlan[] = [];
  
  gamePlans.forEach(gamePlan => {
    const gameDate = new Date(gamePlan.game_date);
    const gameDateOnly = new Date(gameDate.getFullYear(), gameDate.getMonth(), gameDate.getDate());
    
    if (gameDateOnly >= today) {
      futureGames.push(gamePlan);
    } else {
      pastGames.push(gamePlan);
    }
  });
  
  // Sort future games by date (ascending - next game first)
  futureGames.sort((a, b) => {
    return new Date(a.game_date).getTime() - new Date(b.game_date).getTime();
  });
  
  // Sort past games by date (descending - most recent first)
  pastGames.sort((a, b) => {
    return new Date(b.game_date).getTime() - new Date(a.game_date).getTime();
  });
  
  // Combine: future games first, then past games
  return [...futureGames, ...pastGames];
}

/**
 * Check if a game is today
 */
export function isTodayGame(gamePlan: GamePlan): boolean {
  const today = new Date();
  const gameDate = new Date(gamePlan.game_date);
  
  return (
    today.getFullYear() === gameDate.getFullYear() &&
    today.getMonth() === gameDate.getMonth() &&
    today.getDate() === gameDate.getDate()
  );
}

/**
 * Check if this is the next upcoming game
 */
export function isNextGame(gamePlan: GamePlan, allGamePlans: GamePlan[]): boolean {
  const today = new Date();
  const gameDate = new Date(gamePlan.game_date);
  
  // Find all future games (including today)
  const futureGames = allGamePlans.filter(gp => {
    const gpDate = new Date(gp.game_date);
    return gpDate >= today;
  });
  
  if (futureGames.length === 0) return false;
  
  // Sort by date and check if this is the first one
  futureGames.sort((a, b) => new Date(a.game_date).getTime() - new Date(b.game_date).getTime());
  
  return futureGames[0].id === gamePlan.id;
}

/**
 * Get game plan status with user-friendly labels
 */
export function getGamePlanStatus(gamePlan: GamePlan): {
  status: 'today' | 'tomorrow' | 'future' | 'past';
  label: string;
  daysUntil?: number;
  daysAgo?: number;
} {
  const today = new Date();
  const gameDate = new Date(gamePlan.game_date);
  const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const gameDateStart = new Date(gameDate.getFullYear(), gameDate.getMonth(), gameDate.getDate());
  
  const diffTime = gameDateStart.getTime() - todayStart.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  if (diffDays === 0) {
    return { status: 'today', label: 'Today' };
  } else if (diffDays === 1) {
    return { status: 'tomorrow', label: 'Tomorrow' };
  } else if (diffDays > 1) {
    return { status: 'future', label: `In ${diffDays} days`, daysUntil: diffDays };
  } else {
    const daysAgo = Math.abs(diffDays);
    return { status: 'past', label: `${daysAgo} day${daysAgo === 1 ? '' : 's'} ago`, daysAgo };
  }
}